import React, { useContext, useMemo } from 'react';
import { Button, Card, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { TraineeOptionsContext } from '../TraineeOptionsContext';
import {
  BUY,
  SELL,
  CALL_OPTION,
  PUT_OPTION,
  selectors,
  OPTION_CONTRACTS_TYPES,
} from 'state';
import { formatters } from 'services';
import { enterOptionsContract } from '../../../state/app/options/actions';

interface OrderSummaryProps {
  asset_id: number;
  expiration: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  asset_id,
  expiration,
}) => {
  const dispatch = useDispatch();
  const player = useSelector(selectors.player);
  const {
    // selectedOptionsContract,
    selectedOptionType,
    selectedOptionTransaction,
    selectedContractsQuantity,
  } = useContext(TraineeOptionsContext);

  const selectedOptionsContract = useSelector(
    selectors.getSelectedOptionsContract
  );

  const optionsAsset = useSelector(selectors.underlyingAssetById(asset_id));
  const underlyingAsset = useSelector(
    selectors.underlyingAssetById(optionsAsset.non_derivate_asset)
  );
  const { scenario_date } = useSelector(selectors.activeSession);

  // Pick call vs put contract
  const tradedContract = useMemo(() => {
    if (!selectedOptionsContract) return undefined;
    return selectedOptionType === PUT_OPTION
      ? selectedOptionsContract.put
      : selectedOptionsContract.call;
  }, [selectedOptionsContract, selectedOptionType]);

  // days to expiry
  const daysToExpiry = useMemo(() => {
    const scen = new Date(scenario_date);
    const exp = new Date(expiration);
    return Math.ceil((exp.getTime() - scen.getTime()) / (1000 * 60 * 60 * 24));
  }, [scenario_date, expiration]);

  // price depending on buy vs sell
  const pricePerContract =
    selectedOptionTransaction === BUY
      ? tradedContract?.ask
      : tradedContract?.bid;

  // premium = price * 100 * qty, positive for sell, negative for buy
  const premium = useMemo(() => {
    if (pricePerContract == null) return undefined;
    const raw = pricePerContract * 100 * selectedContractsQuantity;
    return selectedOptionTransaction === SELL ? raw : -raw;
  }, [pricePerContract, selectedContractsQuantity, selectedOptionTransaction]);

  return (
    <Card title="Order summary" size="small" style={{ marginTop: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ flex: 1, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>Underlying asset</th>
                <th style={{ textAlign: 'center' }}>Contracts</th>
                <th style={{ textAlign: 'center' }}>Maturity</th>
                <th style={{ textAlign: 'center' }}>Option type</th>
                <th style={{ textAlign: 'center' }}>Strike</th>
                <th style={{ textAlign: 'center' }}>Price</th>
                <th style={{ textAlign: 'center' }}>Premium</th>
                {/*<th style={{ textAlign: 'center' }}></th>*/}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ textAlign: 'center' }}>
                  {underlyingAsset.ticker}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {formatters.commasNoDigits(selectedContractsQuantity)}
                </td>
                <td style={{ textAlign: 'center' }}>{daysToExpiry} days</td>
                <td style={{ textAlign: 'center' }}>
                  {OPTION_CONTRACTS_TYPES[selectedOptionType]}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {selectedOptionsContract?.strike}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {pricePerContract != null
                    ? formatters.commas2Digits(pricePerContract)
                    : '-'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {premium != null
                    ? `${premium > 0 ? '' : '-'}${formatters.commasNoDigits(
                        Math.abs(premium)
                      )}`
                    : '-'}
                </td>
                {/*<td style={{ textAlign: 'center' }}>*/}
                {/*  <Button type={'primary'}>BUY CALL</Button>*/}
                {/*</td>*/}
              </tr>
            </tbody>
          </table>
        </div>
        <Button
          type="primary"
          style={{ marginLeft: 16, height: '45px', width: '110px' }}
          onClick={() => {
            if (tradedContract && selectedOptionsContract) {
              dispatch(
                enterOptionsContract({
                  player_id: player.id,
                  contract: tradedContract,
                  row: selectedOptionsContract,
                  type: selectedOptionType,
                  txn: selectedOptionTransaction,
                  contracts: selectedContractsQuantity,
                  options_asset: optionsAsset.id,
                })
              );
            } else {
              notification.warning({
                message: 'Please re-select desired contract',
              });
            }
          }}
        >
          {/*buy call buy puy sell call sell put*/}
          {selectedOptionTransaction === BUY ? 'BUY' : 'SELL'}{' '}
          {OPTION_CONTRACTS_TYPES[selectedOptionType]}
        </Button>
      </div>
    </Card>
  );
};

export default OrderSummary;
